/**
 * RealtimeClient — singleton WebSocket client for realtime staff updates.
 *
 * Receives server-sent frames from OrgEventsConsumer and calls onMessage with
 * the parsed event. Reconnects automatically with exponential backoff (1s
 * initial, 30s cap) except when closed with code 4401 (auth failure).
 */

import type { Notification } from "@/store/types";

// ── Discriminated union types (task 3.1.1) ───────────────────────────────────

type ReservationAction = "created" | "updated" | "deleted";
type GuestAction = "created" | "updated" | "deleted";

export interface ReservationEvent {
  type: "reservation.event";
  action: ReservationAction;
  id: string;
  location_id: string;
}

export interface TableEvent {
  type: "table.event";
  action: "updated";
  reservation_id: string;
  location_id: string;
}

export interface GuestEvent {
  type: "guest.event";
  action: GuestAction;
  id: string;
  organization_id: string;
}

export interface NotificationStaff {
  type: "notification.staff";
  kind:
    | "reservation_created_public"
    | "reservation_cancelled_public"
    | "no_show_detected"
    | (string & {}); // open union — keeps autocomplete on known kinds while accepting future strings
  severity: Notification["type"]; // single source of truth — store types
  title: string;
  body: string;
  reservation_id: string; // required: all v1 emit points provide it
}

type WaitlistAction = "created" | "seated" | "removed";

export interface WaitlistEvent {
  type: "waitlist.event";
  action: WaitlistAction;
  entry_id: string;
  // ``seated`` carries the reservation id we just created; others omit it.
  reservation_id?: string;
  // Mirrors WaitlistStatus on the entry post-transition.
  status?: string;
}

export interface UnknownEvent {
  type: string; // unmatched literal — drop quietly
}

export type RealtimeEvent =
  | ReservationEvent
  | TableEvent
  | GuestEvent
  | NotificationStaff
  | WaitlistEvent
  | UnknownEvent;

// ── RealtimeClientOptions ─────────────────────────────────────────────────────

export interface RealtimeClientOptions {
  wsOrigin: string; // e.g. "wss://example.com"
  locationId?: string | null;
  onMessage: (event: RealtimeEvent) => void;
  onAuthFailure: () => void; // called once on 4401, before stopped=true
}

// ── RealtimeClient (task 3.2.8) ───────────────────────────────────────────────

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private stopped = false; // true → never reconnect (4401 / explicit close)
  private currentLocationId: string | null = null;
  private opts!: RealtimeClientOptions;

  configure(opts: RealtimeClientOptions): void {
    this.opts = opts;
    this.currentLocationId = opts.locationId ?? null;
    // Reset stopped so a fresh configure+connect (after logout/login) works.
    this.stopped = false;
  }

  connect(): void {
    this.stopped = false;
    this.openSocket();
  }

  close(): void {
    this.stopped = true;
    if (this.socket) {
      this.socket.close(1000);
      this.socket = null;
    }
  }

  setLocation(newId: string | null): void {
    if (newId === this.currentLocationId) return;
    this.currentLocationId = newId;
    if (this.socket) {
      this.socket.close(1000);
      this.socket = null;
    }
    if (!this.stopped) {
      this.openSocket();
    }
  }

  private url(): string {
    const base = `${this.opts.wsOrigin}/ws/events/`;
    return this.currentLocationId ? `${base}?location=${this.currentLocationId}` : base;
  }

  private openSocket(): void {
    const ws = new WebSocket(this.url());
    this.socket = ws;

    const onOpen = () => {
      this.reconnectAttempts = 0;
    };

    const onMessage = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as RealtimeEvent;
        this.opts.onMessage(parsed);
      } catch {
        // Malformed JSON — ignore silently.
      }
    };

    const onClose = (event: CloseEvent) => {
      this.socket = null;
      this.onSocketClose(event);
    };

    ws.addEventListener("open", onOpen as EventListenerOrEventListenerObject);
    ws.addEventListener("message", onMessage as EventListenerOrEventListenerObject);
    ws.addEventListener("close", onClose as EventListenerOrEventListenerObject);
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const delay =
      Math.min(30000, 1000 * 2 ** this.reconnectAttempts) + Math.floor(Math.random() * 500);
    this.reconnectAttempts++;
    setTimeout(() => {
      if (!this.stopped) {
        this.openSocket();
      }
    }, delay);
  }

  private onSocketClose(ev: CloseEvent): void {
    if (ev.code === 4401) {
      this.opts.onAuthFailure();
      this.stopped = true;
      return;
    }
    this.scheduleReconnect();
  }
}

// Module-level singleton — instantiated but NOT connected at import time.
export const realtimeClient = new RealtimeClient();
